/**
 * Tier 2 confirmation — one click, reusing `ConfirmModal.tsx` (`tone="amber"`),
 * never `window.confirm`. Settled by Task 13's brief: no typed name, no code —
 * tier 2 is reversible.
 *
 * `restart`/`lock`/`unlock` get plain copy. `apply_policy` names each
 * restriction and warns, in a rose callout, that it disables ADB on the unit
 * (the mockup doesn't spell this one out, so the copy is written to the same
 * register-name + consequence pattern as the rest of the tier). `push_version`
 * adds a `SearchableDropdown` build picker (never a native `<select>`) —
 * populated from `releaseApi.builds.list`, keyed off the device's `appId` and
 * the current stage — and the Confirm button stays disabled until a build is
 * chosen, via `ConfirmModal`'s `confirmDisabled` prop.
 */
import { useEffect, useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import type { FleetActionId } from '@platform/shared-types';
import ConfirmModal from '../../../components/credits/ConfirmModal';
import SearchableDropdown from '../../../components/SearchableDropdown';
import { releaseApi } from '../../../api/releaseApi';
import type { FleetRow } from './fleetRows';

const RESTRICTIONS = ['ADB off', 'OEM unlock off', 'Safe boot off'];

const detectEnv = (): 'dev' | 'prod' => {
  try {
    const v = (import.meta as any)?.env?.VITE_STAGE;
    if (v === 'prod') return 'prod';
  } catch { /* ignore */ }
  const h = typeof window !== 'undefined' ? window.location.hostname : '';
  if (h.includes('localhost') || h.startsWith('127.') || h.startsWith('admin.dev.')) return 'dev';
  if (h.includes('stage')) return 'dev';
  return h.startsWith('admin.') && !h.includes('dev') && !h.includes('stage') ? 'prod' : 'dev';
};

export interface TierTwoConfirmProps {
  row: FleetRow;
  action: FleetActionId;
  busy: boolean;
  onCancel: () => void;
  onConfirm: (params?: Record<string, unknown>) => void;
}

export default function TierTwoConfirm({ row, action, busy, onCancel, onConfirm }: TierTwoConfirmProps) {
  const registerName = row.registerName || row.deviceId || 'this till';

  const [build, setBuild] = useState<string | null>(null);
  const [builds, setBuilds] = useState<{ value: string; label: string }[]>([]);
  const [buildsLoading, setBuildsLoading] = useState(false);
  const [buildsError, setBuildsError] = useState<string | null>(null);

  useEffect(() => {
    if (action !== 'push_version') return;
    let live = true;
    const appId = row.appId;
    if (!appId) {
      setBuildsError('This device has no known app id — cannot list builds.');
      return;
    }
    setBuildsLoading(true);
    setBuildsError(null);
    releaseApi.builds.list(appId, detectEnv())
      .then((res) => {
        if (!live) return;
        setBuilds((res.items || []).map((b) => ({ value: b.version, label: b.version })));
      })
      .catch((e: any) => {
        if (!live) return;
        setBuildsError(e?.message || 'Could not load builds');
      })
      .finally(() => { if (live) setBuildsLoading(false); });
    return () => { live = false; };
  }, [action, row.appId]);

  const titleFor: Record<string, string> = {
    restart: `Restart ${registerName}?`,
    lock: `Lock ${registerName}?`,
    unlock: `Unlock ${registerName}?`,
    apply_policy: `Apply lockdown policy — ${registerName}?`,
    push_version: `Push app version — ${registerName}?`,
  };

  let message: React.ReactNode;
  switch (action) {
    case 'restart':
      message = 'About a minute. The cashier gets a 15-second warning.';
      break;
    case 'lock':
      message = 'Freeze the screen with a message. Reversible from here.';
      break;
    case 'unlock':
      message = 'Clears the freeze and returns control to the cashier.';
      break;
    case 'apply_policy':
      message = (
        <div className="space-y-3">
          <ul className="list-disc space-y-1 pl-4">
            {RESTRICTIONS.map((r) => <li key={r}>{r}</li>)}
          </ul>
          <div className="flex items-start gap-2 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-[12px] font-semibold text-rose-700">
            <AlertTriangle size={14} className="mt-0.5 shrink-0" />
            This disables ADB on the unit. Re-enabling it needs another lockdown change.
          </div>
        </div>
      );
      break;
    case 'push_version':
      message = (
        <div className="space-y-2">
          <p>Install a specific build now, ignoring the ring.</p>
          {buildsError ? (
            <div role="alert" className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-[12px] font-semibold text-rose-700">
              {buildsError}
            </div>
          ) : (
            <SearchableDropdown
              label="Build"
              options={builds}
              value={build}
              onChange={setBuild}
              placeholder={buildsLoading ? 'Loading builds…' : 'Choose a build…'}
              disabled={buildsLoading}
            />
          )}
        </div>
      );
      break;
    default:
      message = `Send this command to ${registerName}?`;
  }

  const confirmDisabled = action === 'push_version' && !build;

  return (
    <ConfirmModal
      open
      title={titleFor[action] || `${action.replace(/_/g, ' ')} — ${registerName}?`}
      message={message}
      tone="amber"
      confirmLabel={action === 'push_version' ? 'Push version' : 'Confirm'}
      busy={busy}
      confirmDisabled={confirmDisabled}
      onCancel={onCancel}
      onConfirm={() => {
        if (confirmDisabled) return;
        onConfirm(action === 'push_version' ? { targetVersion: build } : undefined);
      }}
    />
  );
}
